import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI, getErrorMessage } from '../../utils/api';
import { formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, XCircle } from 'lucide-react';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminWithdrawals = () => {
  const [rawWithdrawals, setRawWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchWithdrawals();
  }, [debouncedSearch]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getWithdrawals({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawWithdrawals(response.data.withdrawals);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const filteredWithdrawals = statusFilter
    ? rawWithdrawals.filter((w) => w.status === statusFilter)
    : rawWithdrawals;
  const total = filteredWithdrawals.length;
  const withdrawals = filteredWithdrawals.slice((page - 1) * pageSize, page * pageSize);

  const handleWithdrawalAction = async (withdrawalId, action) => {
    try {
      await adminAPI.withdrawalActionById(withdrawalId, { action });
      toast.success(`Withdrawal ${action}`);
      fetchWithdrawals();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const filterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  if (loading && rawWithdrawals.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-withdrawals">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Withdrawal Management</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Amount</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Destination</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Date</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="table-row" data-testid={`withdrawal-row-${withdrawal.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{withdrawal.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{withdrawal.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground">{withdrawal.asset_symbol}</td>
                  <td className="py-4 text-foreground">{withdrawal.amount}</td>
                  <td className="py-4 text-muted-foreground text-sm">{withdrawal.destination}</td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(withdrawal.status)}`}>
                      {formatStatusText(withdrawal.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(withdrawal.created_at)}</td>
                  <td className="py-4">
                    {(withdrawal.status === 'pending' || withdrawal.status === 'processing') && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleWithdrawalAction(withdrawal.id, 'completed')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 transition-colors"
                          data-testid={`approve-withdrawal-btn-${withdrawal.id}`}
                        >
                          <CheckCircle size={12} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleWithdrawalAction(withdrawal.id, 'rejected')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`reject-withdrawal-btn-${withdrawal.id}`}
                        >
                          <XCircle size={12} />
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>
    </div>
  );
};

export default AdminWithdrawals;
